import React, { useEffect, useRef, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { useQuery } from 'hooks/useQuery';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import Grid from '@mui/material/Grid';
import { generateFirearmListTemplate } from 'requests/firearm';
import { FormSelect } from 'components/molecules/FormSelect';
import { findInDictionary, mapDictionaryToOptionProp } from 'helpers/dictionary';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { Stack, Typography } from '@mui/material';
import { StyledDetailsWrapper } from 'pages/Firearms/RegisteredFirearmDeleteCommentPage/styles';
import { getLegalEntity, getOrgLegalEntity } from 'requests/legalEntities';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { Loader } from 'components/atoms/Loader';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { colors } from 'theme/colors';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EIconTypes } from 'constants/Icons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { FormInput } from 'components/molecules/FormInput';
import {
  StyledFirearmListBox,
  StyledIconWrapper,
} from 'pages/LegalEntitiesPage/LegalEntityAddFirearmPage/styles';
import Icon from 'components/atoms/Icon/Icon';
import { AddFirearmListItem } from 'components/organisms/AddFirearmListItem';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from '@mui/material/Link';
import { useDictionary } from 'models/dictionaryContext';
import {
  EDictionaryTypes,
  ETransactionDictionaryItemTypes,
  IDictionary,
} from 'models/dictionary';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { EMIMETypes } from 'constants/MIMETypes';
import { createInitialTransaction } from 'requests/transaction';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import { ImportFirearms } from 'components/organisms/ImportFirearms';
import { useLang } from 'models/langContext';
import {
  ECustomFieldObjectType,
  ECustomFieldType,
  ICustomFieldByObjectType,
} from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { useAddFirearmFormSchema } from 'pages/LegalEntitiesPage/LegalEntityAddFirearmPage/schemaValidation';
import { ICustomFieldForm } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { isFRTLocal } from 'pages/FRT/helpers';
import { convertSimpleDate, convertToSimpleDate, formatDateTimeToIso } from 'utils/date';
import { FormDateTimePicker } from 'components/molecules/FormDateTimePicker';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { useScroll } from 'hooks/useScroll';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { removeFileExtension } from 'utils/files';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { StyledLocationMarkConfirmation } from 'pages/LegalEntitiesPage/LegalEntityAddTransactionForGroupPage/styles';
import { FormModal } from 'components/molecules/Modal';
import { getAttachmentError } from 'helpers/errors';
import { SelectLocationTransactionPage } from 'pages/Transactions/SelectLocationTransactionPage';
import { typesWithoutStrictAddressLocation } from '../../../utils/transactionUtils';

const MAX_FILE_SIZE_IN_MB = 3;

enum EMarkingType {
  IMPORT = 'IMPORT',
  APPLIED = 'APPLIED',
}

export const TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_IMPORTED,
];

export const TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD_NOT_MANDATORY = [
  ETransactionTypes.DECLARED_IMPORTED,
];

const TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS = [
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
];

export const TRANSACTION_ILLEGAL = [
  ETransactionTypes.SURRENDERED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
];

const individualTransactionTypes = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_IMPORTED,
];

const organizationTransactionTypes = [
  ETransactionTypes.DECLARED_MANUFACTURED,
  ETransactionTypes.DECLARED_UNKNOWN_ORIGIN,
  ETransactionTypes.DECLARED_IMPORTED,
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SURRENDERED,
];
const isShowTracingDocumentsGeneratedMessage = [
  ETransactionTypes.SEIZED,
  ETransactionTypes.RECOVERED,
  ETransactionTypes.SURRENDERED,
];

const LegalEntityAddFirearmPage = () => {
  const { t } = useTranslations();

  const navigate = useNavigate();
  const { state: routerState }: any = useLocation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const initialFormValues: Record<string, any> = {
    typeUniqueId: routerState?.typeUniqueId || '',
    validationOff: false,
    transactionDate: routerState?.transactionDate || '',
    firearmsQuantity: routerState?.firearmsQuantity || 'one',
    serialNumber: routerState?.serialNumber || '',
    serialNumberConfirmation: routerState?.serialNumberConfirmation || '',
    importMarking: routerState?.importMarking || '',
    appliedMarking: routerState?.appliedMarking || '',
    exhibitNumber: routerState?.exhibitNumber || '',
    isExhibitNumberChecked: routerState?.isExhibitNumberChecked || false,
    manufacturedAt: routerState?.manufacturedAt || null,
    isCreateGroup: routerState?.isCreateGroup || 'no',
    groupName: routerState?.groupName || '',
    countryOfImportUniqueId: routerState?.countryOfImportUniqueId || '',
    importer: routerState?.importer || '',
    firearmsList: routerState?.firearmsList?.length
      ? routerState?.firearmsList
      : [
          {
            serialNumber: '',
            serialNumberConfirmation: '',
            importMarking: '',
            appliedMarking: '',
            exhibitNumber: '',
            isExhibitNumberChecked: false,
            ...(TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD?.includes(
              routerState?.typeUniqueId,
            )
              ? { manufacturedAt: null }
              : {}),
          },
        ],
    attachments: routerState?.attachments?.length ? routerState?.attachments : [],
    address: routerState?.address || '',
    unassignedLocation: routerState?.unassignedLocation || false,
    latitude: routerState?.coords ? routerState?.coords[0] : routerState?.latitude,
    longitude: routerState?.coords ? routerState?.coords[1] : routerState?.longitude,
    addressId: routerState?.addressId || null,
    countryUniqueId: routerState?.countryUniqueId || undefined,
    city: routerState?.city || undefined,
  };

  const [legalEntity, setLegalEntity] = useState<ILegalEntityData | null>();
  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState<boolean>(false);
  const [orgLegalEntity, setOrgLegalEntity] = useState<IOrgLegalEntityData | null>();
  const [isOrgLegalEntityLoading, setIsOrgLegalEntityLoading] = useState<boolean>(false);
  const [isErrorLocation, setIsErrorLocation] = useState<boolean>(false);
  const { loadDictionaries, getCustomField, customFieldsFirearms } = useLegalEntities();
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const { hasPermissions } = usePermission();
  const {
    getTransactionInitialTypes,
    transactionInitialTypes,
    getMake,
    make,
    getModel,
    model,
    manufacturer,
    getManufacturer,
    caliber,
    getCaliber,
    firearmAction,
    getFirearmAction,
  } = useDictionary();
  const { setAlert } = useAlert();
  const [selectedFirearm, setSelectedFirearm] = useState<any>(null);
  const [frtDetailsTitle, setFrtDetailsTitle] = useState<string>('');
  const [createInitialTransactionError, setCreateInitialTransactionError] =
    useState<string>('');
  const [showImportDialog, setShowImportDialog] = useState<boolean>(false);
  const [isExhibitNumberChecked, setIsExhibitNumberChecked] = useState<boolean>(
    routerState?.isExhibitNumberChecked || false,
  );
  const [isReloadCausedLocationSelect, setIsReloadCausedLocationSelect] = useState(
    routerState?.isReloadCausedLocationSelect || false,
  );
  const [unassignedLocationModalOpened, setUnassignedLocationModalOpened] = useState(false);

  const [isDataImportedFromFile, setIsDataImportedFromFile] = useState<boolean>(false);
  const { selectedLanguage } = useLang();
  const { countryAll } = useDictionary();
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const { elRef, scrollToError } = useScroll();
  const { shortDateFormat } = useGlobalProperty();

  const { schemaLE } = useAddFirearmFormSchema(customFieldsFirearms);
  const {
    control,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
    getValues,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: initialFormValues,
    resolver: yupResolver(schemaLE) as unknown as Resolver,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'firearmsList',
  });
  const attachments = useFieldArray({
    control,
    name: 'attachments',
  });

  const firearmsQuantityValue = watch('firearmsQuantity');
  const transactionTypeValue = watch('typeUniqueId');
  const isCreateGroupValue = watch('isCreateGroup');
  const transactionDate = watch('transactionDate');
  const unassignedLocation = watch('unassignedLocation');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  const address = watch('address');
  const addressId = watch('addressId');

  const isShowFirearmList = firearmsQuantityValue === 'many';
  const isShowCountry = transactionTypeValue === ETransactionTypes.DECLARED_IMPORTED;
  const isShowGroupNameFormElement = isCreateGroupValue === 'yes';
  const isShowManufacturedDate =
    TRANSACTION_INITIAL_TYPES_FOR_MANUFACTURED_DATE_FIELD.includes(transactionTypeValue);
  const isExhibitNumber =
    TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS.includes(transactionTypeValue);
  const isTransactionIllegal = TRANSACTION_ILLEGAL.includes(transactionTypeValue);
  const isAppliedMarking =
    transactionTypeValue === ETransactionTypes.DECLARED_UNKNOWN_ORIGIN ||
    transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED ||
    transactionTypeValue === ETransactionTypes.SEIZED ||
    transactionTypeValue === ETransactionTypes.RECOVERED ||
    transactionTypeValue === ETransactionTypes.SURRENDERED;

  const isImportMarking = transactionTypeValue === ETransactionTypes.DECLARED_IMPORTED;

  const firearmsQuantityOptions = [
    {
      value: 'one',
      label: t('addFirearm.firearmDetails.section.one.radio'),
    },
    {
      value: 'many',
      label: t('addFirearm.firearmDetails.section.many.radio'),
    },
  ];

  const createGroupOptions = [
    {
      value: 'yes',
      label: t('general.yes.label'),
    },
    {
      value: 'no',
      label: t('general.no.label'),
    },
  ];

  useEffect(() => {
    customFieldsFirearms.forEach((customField) => {
      if (routerState && routerState[customField.name]) {
        const value = routerState[customField.name] || '';
        setValue(customField.name, value);
      }
    });
  }, [customFieldsFirearms]);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.CountryAll]);
      await getCustomField(ECustomFieldObjectType.FIREARM);
      getTransactionInitialTypes(ETransactionDictionaryItemTypes.FIREARM);
      getCaliber();
      getManufacturer();
      getMake();
      getModel();
      getFirearmAction();
    })();
  }, [selectedLanguage]);

  useEffect(() => {
    if (routerState && routerState.isExhibitNumberChecked && !isExhibitNumberChecked) {
      setIsExhibitNumberChecked(true);
    }
  }, []);

  useEffect(() => {
    if (type === ELegalEntityTypes.individual) {
      (async function getLegalEntityDataInit() {
        setIsLegalEntityLoading(true);
        if (id) {
          const response = await getLegalEntity(id);
          if (response) {
            setLegalEntity(response);
          }
          setIsLegalEntityLoading(false);
        }
      })();
    } else if (type === ELegalEntityTypes.organisation) {
      (async function getLegalEntityDataInit() {
        setIsOrgLegalEntityLoading(true);
        if (id) {
          const response = await getOrgLegalEntity(id);
          if (response) {
            setOrgLegalEntity(response);
          }
          setIsOrgLegalEntityLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (routerState?.firearm) {
      setSelectedFirearm(routerState.firearm);
    }
  }, [routerState]);

  useEffect(() => {
    if (transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED) {
      if (firearmsQuantityValue === 'one') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          setValue('manufacturedAt', manufacturedAt);
        } else {
          setValue('manufacturedAt', transactionDate);
        }
      } else if (firearmsQuantityValue === 'many') {
        if (transactionDate instanceof Date) {
          const manufacturedAt: Date = new Date(transactionDate);
          manufacturedAt.setHours(0);
          manufacturedAt.setMinutes(0);
          manufacturedAt.setSeconds(0);
          manufacturedAt.setMilliseconds(0);
          fields.forEach((_, index) => {
            setValue(`firearmsList[${index}].manufacturedAt`, manufacturedAt);
          });
        }
        fields.forEach((_, index) => {
          setValue(`firearmsList[${index}].manufacturedAt`, transactionDate);
        });
      }
    }
  }, [
    transactionTypeValue,
    transactionDate,
    firearmsQuantityValue,
    setValue,
    fields,
    isShowFirearmList,
  ]);

  const filteredTransactionInitialTypes = useRef<IDictionary[] | null>([]);
  const breadcrumbs = useRef([
    { label: t('addFirearm.legalEntities.breadcrumb'), route: ERouteLinks.LegalEntities },
    {
      label:
        type === ELegalEntityTypes.individual
          ? t('addFirearm.individual.breadcrumb')
          : t('addFirearm.organisation.breadcrumb'),
      route: `${ERouteLinks.LegalEntities}?expand=${
        type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
      }`,
    },
    {
      label: t('addFirearm.legalEntityDetails.breadcrumb'),
      route: `${ERouteLinks.ViewLegalEntity.replace(':id', id)}?type=${type}&expand=firearms`,
    },
    t('addFirearm.addFirearm.breadcrumb'),
  ]);

  breadcrumbs.current = [
    { label: t('addFirearm.legalEntities.breadcrumb'), route: ERouteLinks.LegalEntities },
    {
      label:
        type === ELegalEntityTypes.individual
          ? t('addFirearm.individual.breadcrumb')
          : t('addFirearm.organisation.breadcrumb'),
      route: `${ERouteLinks.LegalEntities}?expand=${
        type === ELegalEntityTypes.individual ? 'individual' : 'organisation'
      }`,
    },
    {
      label: t('addFirearm.legalEntityDetails.breadcrumb'),
      route: `${ERouteLinks.ViewLegalEntity.replace(':id', id)}?type=${type}&expand=firearms`,
    },
    t('addFirearm.addFirearm.breadcrumb'),
  ];

  useEffect(() => {
    if (transactionInitialTypes) {
      if (type === ELegalEntityTypes.individual) {
        filteredTransactionInitialTypes.current = transactionInitialTypes?.filter(
          ({ uniqueId: id1 }) =>
            !TRANSACTION_INITIAL_TYPES_ONLY_FOR_ORG_IDS.some((id2) => id2 === id1),
        );
      } else if (type === ELegalEntityTypes.organisation) {
        filteredTransactionInitialTypes.current = transactionInitialTypes;
      }
    }
  }, [type, transactionInitialTypes]);

  useEffect(() => {
    if (selectedFirearm && !isReloadCausedLocationSelect) {
      setValue('isExhibitNumberChecked', false);
      setIsExhibitNumberChecked(false);
    }
  }, [selectedFirearm, transactionTypeValue]);

  const clearUserInput = () => {
    setIsReloadCausedLocationSelect(false);

    setValue('serialNumber', '');
    setValue('serialNumberConfirmation', '');
    setValue('exhibitNumber', '');
    setValue('importMarking', '');
    setValue('appliedMarking', '');
    setValue('manufacturedAt', '');
    setValue('isExhibitNumberChecked', false);
    setValue('importer', '');
    setValue('countryOfImportUniqueId', '');

    customFieldsFirearms.forEach((customField) => setValue(customField.name, ''));
  };

  const handleCreateInitialTransaction = () => {
    setCreateInitialTransactionError('');
    setValue('validationOff', false);
    handleSubmit(async (data) => {
      const getCustomFieldsData = (firearm: any) => {
        const customFieldsPayload: ICustomFieldForm[] = [];
        Object.keys(firearm).map((key) => {
          const customField = customFieldsFirearms.find((item) => item.name === key);
          if (customField && firearm[key]) {
            const customFieldItem: ICustomFieldForm = {
              customFieldUniqueId: customField.uniqueId,
            };
            if (customField.customFieldType === ECustomFieldType.TEXT) {
              if (firearm[key] === undefined || firearm[key] === null) {
                customFieldItem.text = undefined;
              } else if (firearm[key].trim() === '') {
                customFieldItem.text = undefined;
              } else {
                customFieldItem.text = firearm[key];
              }
            } else if (customField.customFieldType === ECustomFieldType.BOOL) {
              customFieldItem.bool = firearm[key] === '1';
            } else if (customField.customFieldType === ECustomFieldType.DATE) {
              customFieldItem.date = formatDateTimeToIso(firearm[key]);
            }
            customFieldsPayload.push(customFieldItem);
            delete firearm[key];
          }
          return false;
        });

        return customFieldsPayload?.length ? { customFields: customFieldsPayload } : {};
      };

      setIsErrorLocation(false);
      const fullDate = formatDateTimeToIso(data.transactionDate);
      if (selectedFirearm === null) {
        setSubmitBlocked(false);
        return setCreateInitialTransactionError(t('addFirearm.youMustAddFirearm.text'));
      }

      if (!(data.latitude && data.longitude) && !data.unassignedLocation && !addressId) {
        setSubmitBlocked(false);
        return setIsErrorLocation(true);
      }

      const firearms = isShowFirearmList
        ? data.firearmsList.map((firearm: any) => ({
            serialNumber: firearm.isExhibitNumberChecked ? null : firearm.serialNumber,
            exhibitNumber: firearm.exhibitNumber || null,
            frtId: selectedFirearm?.id,
            ...getCustomFieldsData({ ...firearm }),
            ...(isShowManufacturedDate &&
            firearm.manufacturedAt &&
            firearm.manufacturedAt !== ''
              ? {
                  manufacturedAt: formatDateTimeToIso(firearm.manufacturedAt),
                }
              : {}),
            specificationId: selectedFirearm?.specificationId,
            ...(firearm.importMarking
              ? {
                  marking: {
                    marking: firearm.importMarking,
                    markingType: EMarkingType.IMPORT,
                  },
                }
              : {}),
            ...(firearm.appliedMarking
              ? {
                  marking: {
                    marking: firearm.appliedMarking,
                    markingType: EMarkingType.APPLIED,
                  },
                }
              : {}),
          }))
        : [];
      const leId =
        type === ELegalEntityTypes.individual
          ? legalEntity?.uniqueId
          : orgLegalEntity?.uniqueId;

      try {
        setSubmitBlocked(true);

        await createInitialTransaction({
          request: {
            typeUniqueId: data.typeUniqueId,
            keeperId: leId,
            ownerId: leId,
            date: fullDate,
            latitude: addressId ? data.latitude : routerState?.coords?.[0],
            longitude: addressId ? data.longitude : routerState?.coords?.[1],
            address: addressId ? data.address : routerState?.address,
            city: addressId ? data.city : routerState?.city,
            countryUniqueId: addressId ? data.countryUniqueId : routerState?.countryUniqueId,
            unassignedLocation: data.unassignedLocation,
            ...(isShowGroupNameFormElement ? { groupName: data.groupName } : {}),
            ...(isShowCountry
              ? {
                  countryOfImportUniqueId: data.countryOfImportUniqueId,
                  importer: data.importer,
                  countryOfExportUniqueId: null,
                }
              : {}),
            firearms: isShowFirearmList
              ? firearms
              : [
                  {
                    serialNumber: !isExhibitNumberChecked ? data.serialNumber : null,
                    exhibitNumber: data.exhibitNumber || null,
                    frtId: selectedFirearm.id,
                    ...getCustomFieldsData({ ...data }),
                    specificationId: selectedFirearm?.specificationId,
                    ...(isShowManufacturedDate &&
                    data.manufacturedAt &&
                    data.manufacturedAt !== ''
                      ? {
                          manufacturedAt: formatDateTimeToIso(data.manufacturedAt),
                        }
                      : {}),
                    ...(data.importMarking
                      ? {
                          marking: {
                            marking: data.importMarking,
                            markingType: EMarkingType.IMPORT,
                          },
                        }
                      : {}),
                    ...(data.appliedMarking
                      ? {
                          marking: {
                            marking: data.appliedMarking,
                            markingType: EMarkingType.APPLIED,
                          },
                        }
                      : {}),
                  },
                ],
          },
          ...(data.attachments?.filter((attachment: any) => attachment.file !== null)?.length
            ? { attachments: data.attachments }
            : {}),
        }).then(() => {
          if (isShowTracingDocumentsGeneratedMessage.includes(transactionTypeValue)) {
            if (isShowFirearmList && fields.length > 1) {
              setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
                text: t('legalEntityDetails.firearmsAdded.tracingDocumentGenerated.alert'),
              });
            } else {
              setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
                text: t('legalEntityDetails.firearmAdded.tracingDocumentGenerated.alert'),
              });
            }
          } else if (isShowFirearmList && fields.length > 1) {
            setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
              text: t('legalEntityDetails.firearmsAdded.alert'),
            });
          } else {
            setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, {
              text: t('legalEntityDetails.firearmAdded.alert'),
            });
          }
          navigate(
            `${ERouteLinks.ViewLegalEntity.replace(':id', id)}?type=${type}&expand=firearms`,
          );
        });
      } catch (e: any) {
        setSubmitBlocked(false);
        setCreateInitialTransactionError(e.response.data?.message);
      }
      return true;
    })();
  };

  useEffect(() => {
    if (routerState?.firearm && make && model && manufacturer && firearmAction && caliber) {
      const {
        makeUniqueId,
        modelUniqueId,
        actionUniqueId,
        manufacturerUniqueId,
        caliberUniqueId,
      } = routerState.firearm;

      const manufacturerName = manufacturerUniqueId
        ? `${findInDictionary(manufacturerUniqueId, manufacturer)}, `
        : '';
      const calibreName = caliberUniqueId
        ? `, ${findInDictionary(caliberUniqueId, caliber)}`
        : '';

      setFrtDetailsTitle(
        `${findInDictionary(makeUniqueId, make)}, ${findInDictionary(modelUniqueId, model)}, ${manufacturerName} ${findInDictionary(actionUniqueId, firearmAction)}${calibreName} `,
      );
    }
  }, [routerState?.firearm, make, model, manufacturer, firearmAction, caliber]);

  const removeFirearmButtonProps = {
    ...(isDesktop ? { icon: EIconTypes.delete } : { label: 'Delete' }),
    ...(isDesktop ? {} : { size: EButtonSizes.small }),
    ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
    ...(isDesktop
      ? { variant: EButtonVariants.outlinedIconTertiary }
      : { variant: EButtonVariants.outlined }),
    fullWidth: !isDesktop,
  };

  const selectFirearmLabelButton = routerState?.firearm
    ? t('addFirearm.firearmDetails.section.selectFirearm.button')
    : t('addFirearm.firearmDetails.section.selectFirearmReference.button');

  const validateFile = (file: File, index: number) => {
    const fileType: any = file.type;
    if (file) {
      if (file.size === 0) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.emptyFile.text'),
        });
        return false;
      }
      const fileSizeInMB = file.size / 1024 / 1024;
      if (fileSizeInMB > MAX_FILE_SIZE_IN_MB) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileSizeValidation.text'),
        });
        return false;
      }
      if (
        ![
          EMIMETypes.JPG,
          EMIMETypes.JPEG,
          EMIMETypes.PNG,
          EMIMETypes.PDF,
          EMIMETypes.XLSX,
        ].includes(fileType)
      ) {
        setError(`attachments[${index}].file`, {
          type: 'custom',
          message: t('addAttachment.fileTypeValidation.text'),
        });
        return false;
      }
    }
    clearErrors(`attachments[${index}].file`);
    return true;
  };

  const handleUploadFile = (file: File, index: number) => {
    if (validateFile(file, index)) {
      attachments.update(index, {
        file,
      });
    }
  };

  const handleExhibitNumberChange = () => {
    setIsExhibitNumberChecked((prevState) => !prevState);
    setValue('serialNumber', '');
    setValue('serialNumberConfirmation', '');
  };

  const handleGenerateFirearmListTemplate = async () => {
    const data = getValues();
    const getCustomFieldsData = (firearm: any) => {
      const customFieldsPayload: ICustomFieldForm[] = [];
      Object.keys(firearm).map((key) => {
        const customField = customFieldsFirearms.find((item) => item.name === key);
        if (customField && firearm[key]) {
          const customFieldItem: ICustomFieldForm = {
            customFieldUniqueId: customField.uniqueId,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (firearm[key] === undefined || firearm[key] === null) {
              customFieldItem.text = undefined;
            } else if (firearm[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = firearm[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            customFieldItem.bool =
              firearm[key] === '1' ? t('general.yes.label') : t('general.no.label');
          } else if (customField.customFieldType === ECustomFieldType.DATE) {
            customFieldItem.date = convertToSimpleDate(firearm[key], shortDateFormat);
          }
          customFieldsPayload.push(customFieldItem);
        }
        return false;
      });

      return customFieldsPayload?.length ? { customFields: customFieldsPayload } : {};
    };

    const body = {
      typeUniqueId: data.typeUniqueId,
      values: data.firearmsList.map((item: any) => ({
        serialNumber: !item.isExhibitNumberChecked ? item.serialNumber : null,
        exhibitNumber: item.exhibitNumber || null,
        ...getCustomFieldsData(item),
        ...(isShowManufacturedDate
          ? {
              manufacturedAt: item.manufacturedAt
                ? convertToSimpleDate(item.manufacturedAt, shortDateFormat)
                : null,
            }
          : {}),
        ...(item.importMarking
          ? { marking: { marking: item.importMarking, markingType: EMarkingType.IMPORT } }
          : {}),
        ...(item.appliedMarking
          ? { marking: { marking: item.appliedMarking, markingType: EMarkingType.APPLIED } }
          : {}),
      })),
    };

    try {
      await generateFirearmListTemplate(body);
    } catch (e: any) {
      console.error(e);
    }
    return true;
  };

  const renderFirearmDetailsActionButtons = (isTemplateFooter: boolean) => (
    <Grid
      container
      size={{ xs: isTemplateFooter ? 'auto' : 12 }}
      sx={{
        display: 'flex',
        justifyContent: isTemplateFooter ? 'flex-end' : 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap',
        borderTop: {
          xs: `1px solid ${colors.primary25} `,
          lg: 'none',
        },
        pt: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
        pb: {
          xs: isTemplateFooter ? 0 : 2,
          lg: 0,
        },
        px: isTemplateFooter ? 0 : 2,
      }}
    >
      {!isDesktop && !isTemplateFooter && (
        <Grid size={{ xs: 'auto' }} sx={{ flexShrink: 0 }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {`${t('actions.general.label')}: `}
          </Typography>
        </Grid>
      )}
      <Grid
        container
        size={{ xs: 'auto' }}
        spacing={2}
        sx={{
          display: {
            xs: isTemplateFooter ? 'none' : 'flex',
            lg: isTemplateFooter ? 'flex' : 'none',
          },
          justifyContent: 'flex-end',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <Grid size={{ xs: 'auto' }}>
          <Button
            {...removeFirearmButtonProps}
            onClick={() => {
              setSelectedFirearm(null);
              window.history.replaceState({}, document.title);
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );

  const renderRemoveAttachmentButton = (index: number) => (
    <Grid
      size={{ xs: 1 }}
      sx={!isDesktop ? { display: 'flex', justifyContent: 'flex-end' } : {}}
    >
      <Button
        variant={EButtonVariants.outlined}
        icon={EIconTypes.delete}
        onClick={() => attachments.remove(index)}
        sizePX={EButtonSizesPX.px32}
        sx={{ ...(isDesktop ? { marginLeft: '20px' } : {}), marginTop: '28px' }}
      />
    </Grid>
  );

  const mapAvailableTransactionType = (dictionaries: IDictionary[] | null) => {
    if (dictionaries) {
      const transactionTypes = dictionaries.filter((item) => {
        if (item) {
          return type === ELegalEntityTypes.individual
            ? individualTransactionTypes.includes(
                item.uniqueId.toString() as ETransactionTypes,
              )
            : organizationTransactionTypes.includes(
                item.uniqueId.toString() as ETransactionTypes,
              );
        }
        return false;
      });
      return mapDictionaryToOptionProp(transactionTypes);
    }
    return [];
  };

  const importItem = (itemRow: string[], customFields: ICustomFieldByObjectType[]) => {
    const getCustomFields = (index: number, customFieldItemRow: string[]) => {
      const res: any = {};
      customFields.map((field) => {
        if (field.customFieldType === ECustomFieldType.BOOL) {
          if (
            (customFieldItemRow &&
              customFieldItemRow[index] &&
              customFieldItemRow[index].toLowerCase().trim() === 'true') ||
            customFieldItemRow[index].toLowerCase().trim() === 'yes' ||
            customFieldItemRow[index].toLowerCase().trim() ===
              t('general.yes.label').toLowerCase()
          ) {
            res[field.name] = '1';
          } else if (
            (customFieldItemRow &&
              customFieldItemRow[index] &&
              customFieldItemRow[index].toLowerCase().trim() === 'false') ||
            customFieldItemRow[index].toLowerCase().trim() === 'no' ||
            customFieldItemRow[index].toLowerCase().trim() ===
              t('general.no.label').toLowerCase()
          ) {
            res[field.name] = '0';
          } else {
            res[field.name] = undefined;
          }
        } else if (field.customFieldType === ECustomFieldType.DATE) {
          res[field.name] =
            convertSimpleDate(customFieldItemRow?.[index], shortDateFormat) ?? '';
        } else {
          res[field.name] = customFieldItemRow?.[index] ?? '';
        }
        index++;
        return false;
      });
      return res;
    };

    if (
      transactionTypeValue === ETransactionTypes.RECOVERED ||
      transactionTypeValue === ETransactionTypes.SURRENDERED ||
      transactionTypeValue === ETransactionTypes.SEIZED
    ) {
      append({
        serialNumber: itemRow[0] ?? '',
        serialNumberConfirmation: itemRow[0] ?? '',
        exhibitNumber: itemRow[1] ?? '',
        appliedMarking: itemRow[2] ?? '',
        ...getCustomFields(3, itemRow),
      });
    } else if (transactionTypeValue === ETransactionTypes.DECLARED_IMPORTED) {
      append({
        serialNumber: itemRow[0] ?? '',
        serialNumberConfirmation: itemRow[0] ?? '',
        importMarking: itemRow[1] ?? '',
        manufacturedAt: convertSimpleDate(itemRow[2], shortDateFormat) ?? '',
        ...getCustomFields(3, itemRow),
      });
    } else if (transactionTypeValue === ETransactionTypes.DECLARED_UNKNOWN_ORIGIN) {
      append({
        serialNumber: itemRow[0] ?? '',
        serialNumberConfirmation: itemRow[0] ?? '',
        appliedMarking: itemRow[1] ?? '',
        manufacturedAt: convertSimpleDate(itemRow[2], shortDateFormat) ?? '',
        ...getCustomFields(3, itemRow),
      });
    } else {
      append({
        serialNumber: itemRow[0] ?? '',
        serialNumberConfirmation: itemRow[0] ?? '',
        ...getCustomFields(2, itemRow),
      });
    }
  };

  const attachmentNames = watch('attachments');

  useEffect(() => {
    attachments.fields.forEach((attachment: any, index) => {
      if (attachment?.file?.name && !attachmentNames[index]?.attachmentName) {
        setValue(
          `attachments.${index}.attachmentName`,
          removeFileExtension(attachment.file.name),
        );
      }
    });
  }, [attachments.fields, attachmentNames]);

  const canSelectFromMap = typesWithoutStrictAddressLocation.includes(
    transactionTypeValue as ETransactionTypes,
  );

  return (
    <div ref={elRef}>
      <Breadcrumbs items={breadcrumbs.current} />
      <Box sx={{ mb: 2 }}>
        <StyledPageTitle variant="h4">{t('addFirearm.addFirearm.header')}</StyledPageTitle>
      </Box>
      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <FormSelect
            options={mapAvailableTransactionType(transactionInitialTypes)}
            name="typeUniqueId"
            setValue={setValue}
            label={`${t('addFirearm.transactionType.label')}* `}
            onSelect={clearUserInput}
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <FormDateTimePicker
            name="transactionDate"
            label={`${t('addFirearm.transactionDate.label')}* `}
            control={control}
            errors={errors}
            currentHour={true}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addFirearm.ownerAndKeeper.section.name')} alwaysExpanded>
            <Box sx={{ mb: 5 }}>
              {type === ELegalEntityTypes.individual && (
                <div>
                  {isLegalEntityLoading ? (
                    <Loader isLoading={isLegalEntityLoading} />
                  ) : (
                    legalEntity && (
                      <StyledDetailsWrapper container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addFirearm.ownerAndKeeper.section.owner.label')}
                          </Typography>
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={legalEntity.uniqueId}
                              legalType={ELegalEntityTypes.individual}
                              labelText={`${legalEntity?.firstName} ${legalEntity?.lastName} `}
                              legalVisibilityUniqueId={legalEntity.visibilityUniqueId}
                            />
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addFirearm.ownerAndKeeper.section.keeper.label')}
                          </Typography>
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={legalEntity.uniqueId}
                              legalType={ELegalEntityTypes.individual}
                              labelText={`${legalEntity?.firstName} ${legalEntity?.lastName} `}
                              legalVisibilityUniqueId={legalEntity.visibilityUniqueId}
                            />
                          </Typography>
                        </Grid>
                      </StyledDetailsWrapper>
                    )
                  )}
                </div>
              )}
              {type === ELegalEntityTypes.organisation && (
                <div>
                  {isOrgLegalEntityLoading ? (
                    <Loader isLoading={isOrgLegalEntityLoading} />
                  ) : (
                    orgLegalEntity && (
                      <StyledDetailsWrapper container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addFirearm.ownerAndKeeper.section.owner.label')}
                          </Typography>
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={orgLegalEntity.uniqueId}
                              legalType={ELegalEntityTypes.organisation}
                              labelText={orgLegalEntity?.organizationName}
                              legalVisibilityUniqueId={orgLegalEntity.visibilityUniqueId}
                            />
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addFirearm.ownerAndKeeper.section.keeper.label')}
                          </Typography>
                          <Typography variant="body2">
                            <StyledLegalLink
                              legalId={orgLegalEntity.uniqueId}
                              legalType={ELegalEntityTypes.organisation}
                              labelText={orgLegalEntity?.organizationName}
                              legalVisibilityUniqueId={orgLegalEntity.visibilityUniqueId}
                            />
                          </Typography>
                        </Grid>
                      </StyledDetailsWrapper>
                    )
                  )}
                </div>
              )}
            </Box>
          </Accordion>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addFirearm.firearmDetails.section.name')} alwaysExpanded>
            <Grid container sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Button
                  fullWidth
                  label={selectFirearmLabelButton}
                  size={EButtonSizes.small}
                  id="add_firearm-seleect_frt_button"
                  variant={EButtonVariants.outlined}
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => {
                    clearUserInput();
                    navigate(
                      `${ERouteLinks.SearchForFRTFirearm.replace(
                        ':id',
                        id as string,
                      )}?type=${type}&expand=firearms`,
                      { state: { ...routerState, ...getValues() } },
                    );
                  }}
                />
              </Grid>
            </Grid>

            {selectedFirearm !== null ? (
              <Grid
                container
                sx={{
                  border: `1px solid ${colors.primary25} `,
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <Grid
                  size={{ xs: 12 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: colors.primary100,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  <Grid sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {frtDetailsTitle}
                    </Typography>
                  </Grid>
                  {renderFirearmDetailsActionButtons(true)}
                </Grid>
                <Grid container size={{ xs: 12 }} rowSpacing={2} sx={{ p: 2 }}>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Stack>
                      {routerState && selectedFirearm && isFRTLocal(selectedFirearm.id) ? (
                        <>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t(
                              'firearmsReferenceTable.firearmDetails.general.accordion.firearmReferenceLocalID.label',
                            )}
                          </Typography>
                          <Typography variant="body2">{selectedFirearm.frtId}</Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                            {t('addFirearm.firearmDetails.section.firearmsReferenceNo.label')}
                          </Typography>
                          <Typography variant="body2">{selectedFirearm.id}</Typography>
                        </>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Link
                      href={ERouteLinks.FRTDetails.replace(':frtId', routerState.firearm.id)}
                      underline="none"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'block', width: '100%' }}
                    >
                      <Button
                        fullWidth
                        label={t(
                          'addFirearm.firearmDetails.section.showFRTInformation.button',
                        )}
                        variant={EButtonVariants.contained}
                        size={EButtonSizes.small}
                      />
                    </Link>
                  </Grid>
                </Grid>
                {renderFirearmDetailsActionButtons(false)}
              </Grid>
            ) : null}
            {selectedFirearm ? (
              <Grid sx={{ mt: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <FormRadioGroup
                    name="firearmsQuantity"
                    label={t('addFirearm.firearmDetails.section.howManyFirearms.text')}
                    options={firearmsQuantityOptions}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                {firearmsQuantityValue === 'one' ? (
                  <Grid size={{ xs: 12 }} container columnSpacing={2}>
                    {isExhibitNumber && (
                      <Grid
                        size={{ xs: 12, lg: 3 }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <FormCheckbox
                          name="isExhibitNumberChecked"
                          label={t(
                            'addFirearm.firearmDetails.section.serialNumberRemoved.label',
                          )}
                          control={control}
                          errors={errors}
                          onChange={handleExhibitNumberChange}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <FormInput
                        name="serialNumber"
                        label={`${t('addFirearm.firearmList.section.serialNumber.label')}${
                          (isTransactionIllegal && !isExhibitNumberChecked) ||
                          !isTransactionIllegal
                            ? '*'
                            : ''
                        } `}
                        blurred
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        charsLimit={20}
                        disabled={isExhibitNumberChecked}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, lg: 3 }}>
                      <FormInput
                        name="serialNumberConfirmation"
                        label={`${t(
                          'addFirearm.firearmList.section.serialNumberConfirmation.label',
                        )}${
                          (isTransactionIllegal && !isExhibitNumberChecked) ||
                          !isTransactionIllegal
                            ? '*'
                            : ''
                        } `}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        disableCopyPaste
                        charsLimit={20}
                        disabled={isExhibitNumberChecked}
                      />
                    </Grid>
                    {isExhibitNumber && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <FormInput
                          name="exhibitNumber"
                          label={`${t('addFirearm.firearmList.section.exhibitNumber.label')}${
                            isTransactionIllegal && isExhibitNumberChecked ? '*' : ''
                          } `}
                          setValue={setValue}
                          control={control}
                          charsLimit={15}
                          errors={errors}
                        />
                      </Grid>
                    )}
                    {isImportMarking && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <FormInput
                          key="importMarking"
                          name="importMarking"
                          label={t('addFirearm.firearmList.section.importMarking.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          charsLimit={100}
                        />
                      </Grid>
                    )}
                    {isAppliedMarking && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <FormInput
                          key="appliedMarking"
                          name="appliedMarking"
                          label={t('addFirearm.firearmList.section.appliedMarking.label')}
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          charsLimit={100}
                        />
                      </Grid>
                    )}
                    {isShowManufacturedDate && (
                      <Grid size={{ xs: 12, lg: 3 }}>
                        <FormDatePicker
                          name="manufacturedAt"
                          label={`${t(
                            'addFirearm.firearmList.section.manufacturedDate.label',
                          )} `}
                          disabled={
                            transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED
                          }
                          control={control}
                          errors={errors}
                        />
                      </Grid>
                    )}
                    {customFieldsFirearms.map((customField) => (
                      <Grid size={{ xs: 12, lg: 3 }} key={customField.uniqueId}>
                        <FormCustomField
                          data={customField}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Grid size={{ xs: 12 }} container columnSpacing={2}>
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormRadioGroup
                        name="isCreateGroup"
                        label={t('addFirearm.firearmDetails.section.toCreateAGroup.text')}
                        options={createGroupOptions}
                        control={control}
                        errors={errors}
                        disabled={hasPermissions([EPermission.MANAGE_GROUPS_LE]) !== true}
                      />
                    </Grid>
                    {isShowGroupNameFormElement && (
                      <Grid size={{ xs: 12, lg: 6 }}>
                        <FormInput
                          key="groupName"
                          name="groupName"
                          label={`${t('addFirearm.firearmDetails.section.groupName.label')}* `}
                          setValue={setValue}
                          control={control}
                          charsLimit={255}
                          errors={errors}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Grid>
            ) : null}
          </Accordion>
        </Grid>
        {isShowFirearmList && (
          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addFirearm.firearmList.section.name')} alwaysExpanded>
              <Grid sx={{ mb: 5 }}>
                <Grid container spacing={3} sx={{ flexWrap: { lg: 'nowrap' }, mb: 2 }}>
                  <StyledFirearmListBox size={{ xs: 12, lg: 6 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {t('addFirearm.firearmList.section.downloadInfo.text')}
                    </Typography>

                    <Grid
                      sx={{
                        mt: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                      }}
                    >
                      <Button
                        onClick={handleGenerateFirearmListTemplate}
                        label={t('addFirearm.firearmList.section.downloadTemplate.button')}
                        icon={EIconTypes.download}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        fullWidth={!isDesktop}
                        disabled={transactionTypeValue === ''}
                      />
                    </Grid>
                  </StyledFirearmListBox>

                  <StyledFirearmListBox size={{ xs: 12, lg: 6 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      {t('addFirearm.firearmList.section.importInfo.text')}
                    </Typography>

                    <Grid
                      sx={{
                        mt: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                      }}
                    >
                      <Button
                        onClick={() => {
                          setShowImportDialog(true);
                        }}
                        label={t('addFirearm.firearmList.section.importFirearms.button')}
                        icon={EIconTypes.download}
                        variant={EButtonVariants.outlined}
                        size={EButtonSizes.small}
                        fullWidth={!isDesktop}
                        disabled={transactionTypeValue === ''}
                      />
                    </Grid>
                  </StyledFirearmListBox>

                  <ImportFirearms
                    open={showImportDialog}
                    handleClose={() => setShowImportDialog(false)}
                    changeFirearmsListValue={(firearmList) => {
                      remove();
                      setIsDataImportedFromFile(true);
                      firearmList.forEach((firearmsListRow: any, index: number) => {
                        if (index !== 0) {
                          importItem(firearmsListRow, customFieldsFirearms);
                        }
                      });
                    }}
                  />
                </Grid>
                {!!fields.length && (
                  <Grid size={{ xs: 12 }} container>
                    <Grid sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        {`${t('addFirearm.firearmList.section.totalRowsCount.text')}: `}
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {fields.length}
                        </Typography>
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      {fields.map((field, index) => (
                        <AddFirearmListItem
                          key={field.id}
                          index={index}
                          arrayName="firearmsList"
                          removeItem={() => remove(index)}
                          fields={fields}
                          control={control}
                          setValue={setValue}
                          errors={errors}
                          isShowManufacturedDate={isShowManufacturedDate}
                          customFields={customFieldsFirearms}
                          getValues={getValues}
                          isExhibitNumber={isExhibitNumber}
                          transactionTypeValue={transactionTypeValue}
                          isDataImportedFromFile={isDataImportedFromFile}
                          setIsDataImportedFromFile={setIsDataImportedFromFile}
                          isReloadCausedLocationSelect={isReloadCausedLocationSelect}
                          isImportMarking={isImportMarking}
                          isAppliedMarking={isAppliedMarking}
                        />
                      ))}
                    </Grid>
                  </Grid>
                )}
                <Grid size={{ xs: 12, lg: 3 }}>
                  <Button
                    onClick={() => {
                      append({
                        serialNumber: '',
                        ...(isImportMarking ? { importMarking: '' } : {}),
                        ...(isAppliedMarking ? { appliedMarking: '' } : {}),
                        ...(isShowManufacturedDate ? { manufacturedAt: '' } : {}),
                      });
                    }}
                    fullWidth
                    label={t('addFirearm.firearmList.addRow.button')}
                    icon={EIconTypes.plus}
                    size={EButtonSizes.small}
                  />
                </Grid>
              </Grid>
            </Accordion>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Accordion title={t('addFirearm.location.section.name')} alwaysExpanded>
            <Grid container sx={{ justifyContent: 'flex-start' }}>
              <Grid container columnSpacing={2} size={{ xs: 12 }} sx={{ mb: 1, pl: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <FormCheckbox
                    name=""
                    id="unassignedLocation"
                    checked={unassignedLocation}
                    label={t('addTransaction.location.section.unassignedLocation.label')}
                    control={control}
                    errors={errors}
                    onChange={(newState) => {
                      if (newState) setUnassignedLocationModalOpened(true);
                      else setValue('unassignedLocation', false);
                    }}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <SelectLocationTransactionPage
                  show={!unassignedLocation}
                  searchAddressesFor={id}
                  canSelectOnlyExistingAddress={!canSelectFromMap}
                  setValue={setValue}
                  addressId={addressId}
                />
              </Grid>
              {!unassignedLocation && (
                <Grid
                  container
                  size={{ xs: 12 }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    columnGap: 3,
                  }}
                >
                  {canSelectFromMap && (
                    <Grid size={{ xs: 12, lg: 6 }}>
                      <FormInput
                        name="address"
                        label={t('addFirearm.location.section.address.text')}
                        control={control}
                        errors={errors}
                        placeholder={address}
                        InputProps={{ readOnly: true }}
                        disabled
                        sx={{ minWidth: '272px' }}
                        helperText={t('addFirearm.location.section.address.helperText')}
                      />
                    </Grid>
                  )}
                  {latitude && longitude && (
                    <StyledLocationMarkConfirmation>
                      <Typography variant="body1" sx={{ color: colors.success900 }}>
                        {t('addTransaction.location.section.locationMarked.text')}
                      </Typography>
                      <StyledIconWrapper>
                        <Icon type={EIconTypes.checkmark} color={colors.white} fontSize={14} />
                      </StyledIconWrapper>
                    </StyledLocationMarkConfirmation>
                  )}
                  {canSelectFromMap && (
                    <Button
                      id="addTransaction_location_select-button"
                      onClick={() =>
                        navigate(
                          `${ERouteLinks.SelectLocation.replace(
                            ':id',
                            id,
                          )}?type=${type}&expand=firearms`,
                          {
                            state: {
                              ...routerState,
                              ...getValues(),
                              coords:
                                latitude && longitude ? [latitude, longitude] : undefined,
                              breadcrumbs: [
                                ...breadcrumbs.current,
                                t('selectLocationOnMap.page.text'),
                              ],
                              leId: id,
                              type,
                              canSelectOnlyExistingAddress: !canSelectFromMap,
                              returnLink: `${ERouteLinks.LegalEntityAddFirearm.replace(
                                ':id',
                                id,
                              )}?type=${type}&expand=firearms`,
                            },
                          },
                        )
                      }
                      fullWidth={!isDesktop}
                      label={t('selectLocationOnMap.page.text')}
                      variant={EButtonVariants.outlined}
                      size={EButtonSizes.small}
                      sx={{ marginTop: '28px' }}
                    />
                  )}
                  {isErrorLocation && (
                    <Grid size={{ xs: 12 }}>
                      <FormErrorLabel
                        label={
                          canSelectFromMap
                            ? t(
                                'addTransaction.location.section.selectLocationOnMap.errorLabel',
                              )
                            : t('addTransaction.location.section.selectLocation.errorLabel')
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </Grid>
          </Accordion>
        </Grid>
        {isShowCountry && (
          <Grid size={{ xs: 12 }}>
            <Accordion title={t('addFirearm.countryOfImport.section.name')} alwaysExpanded>
              <Grid container columnSpacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FormInput
                    name="importer"
                    label={`${t('addFirearm.countryOfImport.section.importer.label')} `}
                    setValue={setValue}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 3 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(countryAll)}
                    name="countryOfImportUniqueId"
                    setValue={setValue}
                    label={`${t('addFirearm.countryOfImport.section.country.label')}* `}
                    control={control}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </Accordion>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Accordion title={`${t('addFirearm.document.section.name')} `} alwaysExpanded>
            <Box>
              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mb: 2 }}>
                    <Alert
                      text={t('general.field.availableAttachmentFormats.text')}
                      variant={EAlertVariants.warning}
                    />
                  </Box>
                </Grid>
                {attachments?.fields?.map((attachment: any, index) => (
                  <Grid
                    container
                    size={{ xs: 12 }}
                    key={attachment.id}
                    columnSpacing={2}
                    sx={{ mb: { xs: 2, md: 0 }, alignItems: 'flex-start' }}
                  >
                    <Grid size={{ md: 6 }} sx={{ minWidth: 0, order: 1, flexGrow: 1 }}>
                      <FormInput
                        name={`attachments[${index}].attachmentName`}
                        label={t('addFirearm.document.section.attachmentName.label')}
                        defaultValue={
                          attachment?.file?.name
                            ? removeFileExtension(attachment?.file?.name)
                            : ''
                        }
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        arrayName="attachments"
                        arrayIndex={index}
                        fieldName="attachmentName"
                        InputProps={{ disabled: attachment?.file === null }}
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 12, md: 3 }}
                      sx={{
                        order: { xs: 3, md: 2 },
                        marginTop: { md: '28px' },
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        isUploadButton
                        onClick={(files) => handleUploadFile(files[0], index)}
                        label={t('addFirearm.document.section.browseFiles.button')}
                        id="add_file-button"
                        icon={EIconTypes.plus}
                        size={EButtonSizes.small}
                        fullWidth
                        isMultiple
                      />
                      {errors && errors.attachments && getAttachmentError(errors, index) && (
                        <FormErrorLabel
                          label={getAttachmentError(errors, index)?.message ?? ''}
                        />
                      )}
                    </Grid>
                    <Grid sx={{ order: { xs: 2, md: 3 }, marginTop: '28px', flexShrink: 0 }}>
                      <Button
                        variant={EButtonVariants.outlined}
                        icon={EIconTypes.delete}
                        onClick={() => attachments.remove(index)}
                        sizePX={EButtonSizesPX.px32}
                      />
                    </Grid>
                  </Grid>
                ))}
                <Grid size={{ xs: 12, lg: 3 }} sx={{ mt: { xs: 2, lg: 0 } }}>
                  <Button
                    onClick={() =>
                      attachments.append({
                        attachmentName: '',
                        file: null,
                      })
                    }
                    fullWidth
                    label={t('addFirearm.document.section.addFile.button')}
                    id="add_file-button"
                    variant={EButtonVariants.outlined}
                    size={EButtonSizes.small}
                  />
                </Grid>
              </Grid>
            </Box>
          </Accordion>
        </Grid>
        <Grid sx={{ mt: 4, justifyContent: 'flex-start' }} size={{ md: 12 }}>
          <Grid sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('addFirearm.validation.firearmReference')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                id="firearms-save-button"
                onClick={handleCreateInitialTransaction}
                fullWidth
                label={t('addFirearm.save.button')}
                variant={EButtonVariants.contained}
                sx={{ mr: 1 }}
                disabled={selectedFirearm === null}
                isLoading={submitBlocked}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Button
                fullWidth
                label={t('addFirearm.cancel.button')}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  navigate(
                    `${ERouteLinks.ViewLegalEntity.replace(
                      ':id',
                      id,
                    )}?type=${type}&expand=firearms`,
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption">
                *{t('addFirearm.mandatoryField.label')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ mt: 4 }}>
          {createInitialTransactionError && (
            <Box sx={{ mb: 2 }}>
              <Alert text={createInitialTransactionError} variant={EAlertVariants.error} />
            </Box>
          )}
        </Grid>
      </Grid>
      <FormModal
        open={unassignedLocationModalOpened}
        onClickClose={() => {
          setUnassignedLocationModalOpened(false);
          setValue('unassignedLocation', false);
        }}
        onClickApprove={() => {
          setValue('unassignedLocation', true);
          setUnassignedLocationModalOpened(false);
        }}
        title={t('transaction.unassigned.modal.title')}
        text={t('transaction.initial.unassigned.modal.text')}
        approveButtonLabel="general.confirm.label"
      />
    </div>
  );
};

export { LegalEntityAddFirearmPage };
